// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useDebounce } from '@/hooks/common/useDebounce'
import type { WorkspaceBillImpactScore } from '@/types/workspace-bill'

/**
 * Hook to manage impact score filtering with debouncing and localStorage persistence
 * Now supports dynamic ranges based on schema configuration and per-workspace state
 */
export function useImpactScoreFilter(
  minValue: number = 0, 
  maxValue: number = 10,
  selectedOrganizationId?: string | null,
  selectedWorkspaceId?: string | null
) {
  // Create a unique key for this workspace
  const storage_key = useMemo(() => {
    if (selectedOrganizationId && selectedWorkspaceId) {
      return `workspaceBillsMinImpactScore_${selectedOrganizationId}_${selectedWorkspaceId}`
    }
    return "workspaceBillsMinImpactScore" // Fallback to global key
  }, [selectedOrganizationId, selectedWorkspaceId])

  // Helper function to get saved value from localStorage
  const get_saved_value = useCallback((key: string): WorkspaceBillImpactScore => {
    const default_value = Math.round(minValue + (maxValue - minValue) * 0.7)
    
    if (typeof window !== 'undefined') {
      const saved_score = localStorage.getItem(key)
      console.log('[useImpactScoreFilter] Checking localStorage for key:', key, 'found:', saved_score)
      console.log('[useImpactScoreFilter] Current range:', { minValue, maxValue }, 'default_value:', default_value)
      
      if (saved_score) {
        const score = parseInt(saved_score)
        if (score >= minValue && score <= maxValue) {
          console.log('[useImpactScoreFilter] Using saved score:', score)
          return score
        } else {
          console.log('[useImpactScoreFilter] Saved score', score, 'out of range [', minValue, '-', maxValue, '], using 70% of max:', default_value)
        }
      } else {
        console.log('[useImpactScoreFilter] No saved score found, using 70% of max:', default_value)
      }
    }
    return default_value
  }, [minValue, maxValue])

  // Initialize with 70% of max value, we'll load the proper value in useEffect
  const default_value = Math.round(minValue + (maxValue - minValue) * 0.7)
  const [slider_value, setSliderValue] = useState<WorkspaceBillImpactScore>(default_value)
  const [is_loading_initial_value, setIsLoadingInitialValue] = useState(true)
  
  // Debounced value for API calls (delays API calls by 500ms)
  const debounced_min_impact_score = useDebounce(slider_value, 500)

  // Load the initial value when the hook first mounts or when workspace changes
  useEffect(() => {
    console.log('[useImpactScoreFilter] Loading value for workspace:', {
      selectedOrganizationId,
      selectedWorkspaceId,
      storage_key,
      minValue,
      maxValue
    })
    
    setIsLoadingInitialValue(true)
    const saved_value = get_saved_value(storage_key)
    console.log('[useImpactScoreFilter] Loaded saved value:', saved_value)
    
    setSliderValue(saved_value)
    setIsLoadingInitialValue(false)
  }, [storage_key, get_saved_value, selectedOrganizationId, selectedWorkspaceId, minValue, maxValue])

  // Save to localStorage when slider value changes (immediate for persistence)
  // Skip saving during initial load
  useEffect(() => {
    // Only save if we're not loading the initial value and have valid org/workspace IDs
    if (!is_loading_initial_value && selectedOrganizationId && selectedWorkspaceId) {
      console.log('[useImpactScoreFilter] Saving value:', { storage_key, slider_value })
      localStorage.setItem(storage_key, slider_value.toString())
    }
  }, [slider_value, storage_key, selectedOrganizationId, selectedWorkspaceId, is_loading_initial_value])

  // Update slider value when the range changes, but only if current value is outside the new range
  // This should run after the initial value is loaded and not interfere with saved values
  useEffect(() => {
    // Only adjust if we're not loading initial value and have valid org/workspace IDs
    if (!is_loading_initial_value && selectedOrganizationId && selectedWorkspaceId) {
      if (slider_value < minValue) {
        console.log('[useImpactScoreFilter] Adjusting slider value to min:', minValue)
        setSliderValue(minValue)
      } else if (slider_value > maxValue) {
        console.log('[useImpactScoreFilter] Adjusting slider value to max:', maxValue)
        setSliderValue(maxValue)
      }
    }
  }, [minValue, maxValue, slider_value, selectedOrganizationId, selectedWorkspaceId, is_loading_initial_value])

  // Handle slider changes (updates immediately for responsive UI)
  const handle_slider_change = useCallback((value: number[]) => {
    console.log('[useImpactScoreFilter] Slider changed to:', value[0])
    setSliderValue(value[0])
  }, [])

  return {
    slider_value,
    debounced_min_impact_score,
    handle_slider_change,
    initial_min_impact_score: slider_value // Return current value as initial
  }
}